import { Component, computed, inject, signal, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { ChartModule } from 'primeng/chart';
import { DatePicker } from 'primeng/datepicker';
import { Skeleton } from 'primeng/skeleton';
import { Tab, TabList, Tabs } from 'primeng/tabs';
import {
  AdminDashboardResponse,
  AdminsClient,
  EnumDtoOfUserPackageStatus,
  UserPackageStatus,
} from '../../../infrastructure/shared/model/fss.api';
import { UserService } from '../../../infrastructure/shared/services/user.service';
import {
  getDateFormatedSr,
  getDateFormatedSrLong,
  getDateFormatedSrWithTime,
} from '../../../infrastructure/shared/services/date.service';
import { Arrow } from '../../../infrastructure/shared/icons/arrow/arrow';
import { ArrowUpRight } from '../../../infrastructure/shared/icons/arrow-up-right/arrow-up-right';
import { Calendar } from '../../../infrastructure/shared/icons/calendar/calendar';
import { XClose } from '../../../infrastructure/shared/icons/x-close/x-close';
import { Wallet05 } from '../../../infrastructure/shared/icons/wallet-05/wallet-05';
import { CalendarCheck02 } from '../../../infrastructure/shared/icons/calendar-check-02/calendar-check-02';
import { SlashCircle01 } from '../../../infrastructure/shared/icons/slash-circle-01/slash-circle-01';
import { ChevronRight } from '../../../infrastructure/shared/icons/chevron-right/chevron-right';
import { formatNumberPlain, formatPrice } from '../../../infrastructure/shared/functions/functions';

type PeriodKey = '7d' | '30d' | '12m' | 'custom';

/** Chart palette — matches the design (blue + dark). */
const BLUE = '#2970FF';
const DARK = '#181D27';

const SR_MONTHS_FULL = [
  'Januar', 'Februar', 'Mart', 'April', 'Maj', 'Jun',
  'Jul', 'Avgust', 'Septembar', 'Oktobar', 'Novembar', 'Decembar',
];
const SR_MONTHS_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'Maj', 'Jun', 'Jul', 'Avg', 'Sep', 'Okt', 'Nov', 'Dec',
];

@Component({
  standalone: true,
  selector: 'app-admin-dashboard',
  imports: [
    FormsModule,
    ChartModule,
    DatePicker,
    Skeleton,
    Tabs,
    TabList,
    Tab,
    Arrow,
    ArrowUpRight,
    Calendar,
    XClose,
    Wallet05,
    CalendarCheck02,
    SlashCircle01,
    ChevronRight,
  ],
  templateUrl: './admin-dashboard.html',
  styleUrl: './admin-dashboard.scss',
})
export class AdminDashboard {
  private _client = inject(AdminsClient);
  private _user = inject(UserService);
  private _router = inject(Router);

  protected readonly periods: { key: PeriodKey; label: string }[] = [
    { key: '7d', label: '7 dana' },
    { key: '30d', label: '30 dana' },
    { key: '12m', label: '12 meseci' },
    { key: 'custom', label: 'Prilagodi' },
  ];

  protected data = signal<AdminDashboardResponse | undefined>(undefined);
  protected loading = signal<boolean>(true);
  protected selectedPeriod = signal<PeriodKey>('12m');
  protected editingCustom = signal<boolean>(false);
  protected range = signal<{ from: Date; to: Date }>(this._computeRange('12m'));

  protected readonly firstName = (this._user.getUserData.fullName || 'korisniče').split(' ')[0];
  protected readonly todayLabel = this._formatTodayLabel(new Date());

  protected rangeLabel = computed(() => {
    const r = this.range();
    return `${getDateFormatedSr(r.from)} – ${getDateFormatedSr(r.to)}`;
  });

  /** Label shown in the custom-range chip, e.g. "Jan 10, 2025 – Jan 16, 2025". */
  protected customRangeLabel = computed(() => {
    const r = this.range();
    return `${this._formatChipDate(r.from)} – ${this._formatChipDate(r.to)}`;
  });

  /** Line chart — appointments per month, or per day when the range fits in a single month. */
  protected appointmentsData = computed(() => {
    const points = this.data()?.monthlyAppointments ?? [];
    return {
      labels: points.map((p) => this._chartPointLabel(p.month, p.day)),
      datasets: [
        {
          data: points.map((p) => p.count ?? 0),
          borderColor: BLUE,
          fill: false,
          tension: 0.4,
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 5,
          pointHoverBackgroundColor: BLUE,
          pointHoverBorderColor: '#FFFFFF',
          pointHoverBorderWidth: 2,
        },
      ],
    };
  });

  protected readonly appointmentsOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: true,
        // Always above the point so it never covers the month labels on the x-axis
        // (Chart.js clamps it inside the canvas when the point is near the top).
        yAlign: 'bottom',
        caretPadding: 8,
        // Single series — the colour box adds nothing (and rendered as an empty square).
        displayColors: false,
        callbacks: {
          title: (items: { dataIndex: number }[]) => {
            const point = this.data()?.monthlyAppointments?.[items[0]?.dataIndex ?? 0];
            const month = SR_MONTHS_FULL[(point?.month ?? 1) - 1] ?? '';
            return point?.day != null ? `${point.day}. ${month.toLowerCase()}` : month;
          },
          label: (item: { parsed: { y: number } }) => `${item.parsed.y} termina`,
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        border: { display: false },
        ticks: { color: '#A4A7AE', font: { size: 11 } },
      },
      y: { display: false, beginAtZero: true },
    },
  };

  /** Bar chart — revenue per package type. */
  protected revenueByPackageData = computed(() => {
    const points = this.data()?.revenueByPackage ?? [];
    return {
      labels: points.map((p) => p.packageName ?? ''),
      datasets: [
        {
          data: points.map((p) => p.revenue ?? 0),
          backgroundColor: BLUE,
          hoverBackgroundColor: BLUE,
          borderRadius: 8,
          barThickness: 28,
        },
      ],
    };
  });

  protected readonly revenueByPackageOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: true,
        displayColors: false,
        callbacks: {
          label: (item: { parsed: { y: number } }) => `${formatPrice(item.parsed.y)}€`,
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        border: { display: false },
        ticks: { color: '#A4A7AE', font: { size: 11 } },
      },
      y: {
        beginAtZero: true,
        grid: { color: '#F0F2F5' },
        border: { display: false },
        ticks: {
          color: '#A4A7AE',
          font: { size: 11 },
          callback: (value: number | string) => `${formatPrice(Number(value))}€`,
        },
      },
    },
  };

  /** Legend rows for the client-type doughnut — label, colour, amount and share. */
  protected revenueByClientTypeLegend = computed(() => {
    const stats = this.data()?.revenueByClientType;
    const rows = [
      { label: 'Pravna lica', color: BLUE, value: stats?.legalEntityRevenue ?? 0 },
      { label: 'Fizička lica', color: DARK, value: stats?.individualRevenue ?? 0 },
    ];
    const total = rows.reduce((sum, r) => sum + r.value, 0);
    return rows.map((r) => ({ ...r, percentage: total ? (r.value / total) * 100 : 0 }));
  });

  /** Doughnut chart — revenue per client type. */
  protected revenueByClientTypeData = computed(() => {
    const legend = this.revenueByClientTypeLegend();
    return {
      labels: legend.map((r) => r.label),
      datasets: [
        {
          data: legend.map((r) => r.value),
          backgroundColor: [BLUE, DARK],
          hoverBackgroundColor: [BLUE, DARK],
          borderWidth: 0,
        },
      ],
    };
  });

  protected readonly revenueByClientTypeOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '70%',
    plugins: {
      // Rendered as HTML below the chart so it can show amounts and shares.
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (item: { label: string; parsed: number }) =>
            `${item.label}: ${formatPrice(item.parsed)}€`,
        },
      },
    },
  };

  constructor() {
    this._load();
  }

  private _rangePicker = viewChild<DatePicker>('rangePicker');

  protected selectPeriod(value: string | number | undefined): void {
    const key = value as PeriodKey;
    if (key === 'custom') {
      this.editingCustom.set(true);
      setTimeout(() => {
        const picker = this._rangePicker();
        picker?.inputfieldViewChild?.nativeElement.focus();
        picker?.showOverlay();
      });
      return;
    }
    this.editingCustom.set(false);
    this.selectedPeriod.set(key);
    this.customModel = [];
    this.range.set(this._computeRange(key));
    this._load();
  }

  /** Two-way bound model for the range date picker. */
  protected customModel: Date[] = [];

  protected onClearCustom(): void {
    this.selectPeriod('12m');
  }

  /** Opens the calendar overlay when clicking anywhere on the input, not just its icon. */
  protected onRangePickerClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (target.closest('.p-datepicker-input-icon')) return;

    const picker = this._rangePicker();
    if (picker && !picker.overlayVisible) {
      picker.showOverlay();
    }
  }

  protected onCustomRange(): void {
    const [from, to] = this.customModel ?? [];
    if (!from || !to) return;
    const fromStart = new Date(from);
    fromStart.setHours(0, 0, 0, 0);
    const toEnd = new Date(to);
    toEnd.setHours(23, 59, 59, 999);
    this.selectedPeriod.set('custom');
    this.editingCustom.set(false);
    this.range.set({ from: fromStart, to: toEnd });
    this._load();
  }

  protected requestBadgeClass(status: EnumDtoOfUserPackageStatus | undefined): string {
    switch (status?.value) {
      case UserPackageStatus.NEW:
        return 'badge--blue';
      case UserPackageStatus.INVOICE_SENT:
        return 'badge--warning';
      default:
        return 'badge--gray';
    }
  }

  /** Status label for an active-requests row; "Novi zahtevi" when there is more than one. */
  protected requestLabel(status: EnumDtoOfUserPackageStatus | undefined, count: number | undefined): string {
    if (status?.value === UserPackageStatus.NEW) {
      return (count ?? 0) === 1 ? 'Novi zahtev' : 'Novi zahtevi';
    }
    return status?.description ?? 'Zahtev';
  }

  protected goToRequests(): void {
    this._router.navigate(['/admin/packages']);
  }

  protected readonly getDateFormatedSrWithTime = getDateFormatedSrWithTime;
  protected readonly abs = Math.abs;
  protected readonly formatPrice = formatPrice;

  /** Plain number, same convention as prices (no grouping, comma decimal): `34,78`. */
  protected readonly formatNumber = formatNumberPlain;

  private async _load(): Promise<void> {
    this.loading.set(true);
    const r = this.range();
    const res = await firstValueFrom(this._client.getAdminDashboard(r.from, r.to));
    this.data.set(res.result);
    this.loading.set(false);
  }

  private _computeRange(key: PeriodKey): { from: Date; to: Date } {
    const to = new Date();
    to.setHours(23, 59, 59, 999);
    const from = new Date();
    from.setHours(0, 0, 0, 0);
    if (key === '7d') from.setDate(from.getDate() - 6);
    else if (key === '30d') from.setDate(from.getDate() - 29);
    else from.setFullYear(from.getFullYear() - 1);
    return { from, to };
  }

  private _chartPointLabel(month: number | undefined, day: number | undefined): string {
    const monthLabel = SR_MONTHS_SHORT[(month ?? 1) - 1];
    return day != null ? `${day}. ${monthLabel.toLowerCase()}` : monthLabel;
  }

  private _formatChipDate(date: Date): string {
    return getDateFormatedSr(date);
  }

  private _formatTodayLabel(date: Date): string {
    return getDateFormatedSrLong(date);
  }
}
